import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import Cookies from "js-cookie";
import TriggerHelp from "../components/TriggerHelp";

import {
  keccak256_hash_string,
  sha3_256_hash_string,
  HASH_ALGORITHM_SHA3_256,
  HASH_ALGORITHM_KECCAK_256,
} from "../utils/cryptoUtils";

import { formatHexStr } from "../utils/formatHexStr";
import CustomAnchor from "./mantineCustomComponents/CustomAnchor";
import {
  Text,
  useMantineTheme,
  CopyButton,
  Tooltip,
  ActionIcon,
  Accordion,
  Loader,
  Alert,
} from "@mantine/core";
import { useStylesStampResult } from "../utils/mantine/useStylesStampResult";
import { Box, Flex, Title } from "@mantine/core";
import { useStylesPage } from "../utils/mantine/useStylesPage";
import CustomTooltip from "./mantineCustomComponents/CustomTooltip";
import { useStylesTooltip } from "../utils/mantine/useStylesTooltip";
import {
  IconCheck,
  IconCopy,
  IconMessageReportFilled,
} from "@tabler/icons-react";

import { useStylesTechnicalDetail } from "../utils/mantine/useStylesTechnicalDetail";
import StampDetail from "./StampDetail";
import { useStylesAccordion } from "../utils/mantine/useStylesAccordion";
import { useStylesIcons } from "../utils/mantine/useStylesIcons";
import DownloadStampCertificate from "./DownloadStampCertificate";
import { getFilePreview, getVerifiedFileDownloadUrl } from "../utils/apis";
import { useStylesFileManager } from "../utils/mantine/useStylesFileManager";
import FileFoundPreview from "./FileFoundPreview";
import CustomButton from "./mantineCustomComponents/CustomButton";
import { formatFileSize } from "../utils/formatFileSize";
import { StampTimeDetailsRows } from "./StampIdentityFields";

const FileFound = ({ data, staticBasePath }) => {
  //css classes
  const { classes: classesStampResult } = useStylesStampResult();
  const { classes: classesPage } = useStylesPage();
  const { classes: tooltipClasses } = useStylesTooltip();
  const { classes: classesTechnicalDetail } = useStylesTechnicalDetail();
  const { classes: classesAccordion } = useStylesAccordion();
  const { classes: classesIcons } = useStylesIcons();
  const { classes: classesFileManager } = useStylesFileManager();

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // current hash
  const [currentHashVersion, setCurrentHashVersion] = useState("");

  // current hash Collection
  const [currentHashCollectionVersion, setCurrentHashCollectionVersion] =
    useState("");

  useEffect(() => {
    setCurrentHashVersion("");

    // check collection hash type
    if (data.collection_name) {
      const hashCollectionSha3 = sha3_256_hash_string(data.collection_name);
      const hashCollectionKeccak256 = keccak256_hash_string(
        data.collection_name,
      );

      const supportedHashTypesCollection = {
        [hashCollectionSha3]: HASH_ALGORITHM_SHA3_256,
        [hashCollectionKeccak256]: HASH_ALGORITHM_KECCAK_256,
      };

      if (supportedHashTypesCollection[data.collection_hash]) {
        //check exists current hash Collection in all hashes
        setCurrentHashCollectionVersion(
          supportedHashTypesCollection[data.collection_hash],
        );
      }
    }
  });

  // Logged-out users have no display preference, so format and label times in UTC.
  const timeZone = data.display_timezone ? data.display_timezone : "UTC";

  const [helpIsActive, setHelpIsActive] = useState(() => {
    const cookieValue = Cookies.get("helpIsActive");
    return cookieValue !== undefined ? JSON.parse(cookieValue) : true;
  });

  const handleHelpTriggerClick = () => {
    const newHelpIsActive = !helpIsActive;
    setHelpIsActive(newHelpIsActive);
    Cookies.set("helpIsActive", JSON.stringify(newHelpIsActive));
  };

  const [isNearBottom, setIsNearBottom] = useState(false);

  const handleScroll = () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const remainingScroll = documentHeight - (scrollTop + windowHeight);

    if (
      (remainingScroll <= 80 && window.innerWidth <= 639) ||
      (remainingScroll <= 48 && window.innerWidth >= 640)
    ) {
      setIsNearBottom(true);
    } else {
      setIsNearBottom(false);
    }
  };

  const [loading, setLoading] = useState(false);

  const [fileUrlError, setFileUrlError] = useState(null);
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const fetchFileUrl = async (fileHash, setLoading) => {
    setFileUrlError(null);
    //  Fetch the download URL
    const linkResponse = await getVerifiedFileDownloadUrl(fileHash);

    if (linkResponse.error) {
      // Set error
      setFileUrlError(linkResponse.error);
    } else if (linkResponse.url) {
      window.location.href = linkResponse.url;
    }
    setLoading(false);
  };

  const useLinkIdFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const hasRelevantParam = params.has("link-id");
    const linkId = hasRelevantParam ? params.get("link-id") : null;
    return { hasRelevantParam, linkId };
  };

  const { hasRelevantParam, linkId } = useLinkIdFromUrl();

  const handleDownload = () => {
    setLoading(true);
    if (hasRelevantParam) {
      fetchFileUrl(linkId, setLoading);
    } else {
      setFileUrlError("File hash parameter is missing from URL.");
    }
  };

  const [fileExtension, setFileExtension] = useState(null);
  const [fileType, setFileType] = useState(null);
  const [fileSize, setFileSize] = useState(null);
  const [preview, setPreview] = useState(null);
  const [filePreviewError, setFilePreviewError] = useState(null);
  const [loadingFilePreview, setLoadingFilePreview] = useState(false);

  useEffect(() => {
    if (linkId) {
      setLoadingFilePreview(true);
      getFilePreview(linkId, setFileSize, setFileExtension).then((result) => {
        if (result.error) {
          setFilePreviewError(result.error);
        } else {
          setFileType(result.fileType);
          setPreview(result.preview);
        }
        setLoadingFilePreview(false);
      });
    }
  }, [linkId]);
  return (
    <Box
      className={`${classesStampResult.stamp} ${classesPage.page} stamp-result file-found`}
    >
      <Box className="file-found-header">
        <Text component="h1">Content Authenticity Record</Text>
        <CopyButton value={`${window.location.href}`} timeout={2000}>
          {({ copied, copy }) => (
            <CustomButton
              text={`(${copied ? "copied" : "copy"} link to this page)`}
              onClick={copy}
              componentClasses="link"
              variant="transparent"
            ></CustomButton>
          )}
        </CopyButton>

        <Flex className="file-found-header-link file-found-header-link-block">
          <Text>Content Access</Text>
          <CustomAnchor
            href="#"
            text="Download Content"
            IconDownloadFile="true"
            componentClasses="link"
            onClick={(e) => {
              e.preventDefault();
              handleDownload();
            }}
          />
          {fileSize && fileExtension && (
            <Text className="file-found-header-link-text">
              {` (${formatFileSize(fileSize)}${
                fileSize && fileExtension ? ", " : ""
              }`}
              <Text component="span">{fileExtension}</Text>
              {`)`}
            </Text>
          )}
        </Flex>

        <FileFoundPreview
          preview={preview}
          filePreviewError={filePreviewError}
          loadingFilePreview={loadingFilePreview}
          fileType={fileType}
        />

        {loading && <Loader className={classesFileManager.linkLoader} />}
        {fileUrlError && (
          <Alert
            variant="transparent"
            className={`${classesFileManager.alert} ${classesFileManager.alertRed}  ${classesFileManager.alertFile}`}
            title="Error"
            icon={<IconMessageReportFilled />}
          >
            {fileUrlError}
          </Alert>
        )}

        <Box className="file-found-preview-container">
          <Box className="block-file-found">
            <Title order={1} className="h1-file-found">
              Authenticity Stamp
            </Title>
            <Text className="file-found-header-text file-found-header-text">
              Backed by tamper-proof blockchain audit trail
            </Text>
          </Box>
        </Box>
      </Box>
      <StampDetail
        data={data}
        staticBasePath={staticBasePath}
        helpIsActive={helpIsActive}
        timeZone={timeZone}
      ></StampDetail>
      <Box
        className={`${classesTechnicalDetail.technicalBlock} ${classesTechnicalDetail.technical}`}
      >
        <Accordion
          className={classesAccordion.accordion}
          value="tech"
          variant="contained"
          chevron={null}
        >
          <Accordion.Item value="tech">
            <Accordion.Control component="div">
              <Flex gap={theme.flex.gap.xs}>
                <Text className="title">Stamp Details</Text>

                {helpIsActive && (
                  <CustomTooltip
                    content={
                      <>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Blockchain Address
                          </Text>{" "}
                          is the blockchain address of the user who made the
                          stamp.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Content ID
                          </Text>{" "}
                          indicates whether vBase has confirmed the identity of
                          the user.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Collection ID
                          </Text>{" "}
                          is a fingerprint of the Collection Name.
                        </Text>
                        <Text
                          classNames={{
                            root: tooltipClasses.tooltipText,
                          }}
                        >
                          <Text component="span" className="b">
                            Public Blockchain Record
                          </Text>{" "}
                          is a link to a third-party tool showing the permanent
                          location of your data on a blockchain!
                        </Text>
                      </>
                    }
                  ></CustomTooltip>
                )}
              </Flex>
            </Accordion.Control>
            <Accordion.Panel>
              <Box>
                <StampTimeDetailsRows data={data} />

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Blockchain Address</Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__account_id">
                    <CustomAnchor
                      text={formatHexStr(data.user_id)}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`/verify/user-data/?user=${data.user_id}`}
                    />
                    <CopyButton value={data.user_id} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    Content ID{" "}
                    {currentHashVersion && <>({currentHashVersion})</>}
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value" id="stamp-found__content-id">
                    {formatHexStr(data.objectCid)}

                    <CopyButton value={data.objectCid} timeout={2000}>
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">
                    {" "}
                    Collection ID{" "}
                    {currentHashCollectionVersion && (
                      <>({currentHashCollectionVersion})</>
                    )}
                  </Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    {data.collection_hash ? (
                      <CustomAnchor
                        text={
                          data.collection_hash
                            ? formatHexStr(data.collection_hash)
                            : "<<None>>"
                        }
                        componentClasses="link collection-hash"
                        color={theme.customColors.black}
                        href={`/verify/user-data/?user=${
                          data.user || data.user_id
                        }&collection_cid=${data.collection_hash}`}
                      />
                    ) : (
                      "<<None>>"
                    )}

                    <CopyButton
                      value={data.collection_hash ?? ""}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            style={{
                              opacity: data.collection_hash ? 1 : 0,
                              pointerEvents: data.collection_hash
                                ? "auto"
                                : "none",
                            }}
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item"
                  align={theme.flex.align.end}
                >
                  <Text className="key">Public Blockchain Record</Text>
                  <Box className="dots"></Box>
                  <Text className="value">
                    <CustomAnchor
                      target="_blank"
                      text={`${data.blockExplorerUrl}${formatHexStr(
                        data.transactionHash,
                        10,
                        0,
                        20,
                        true,
                      )}...`}
                      componentClasses="link"
                      color={theme.customColors.black}
                      href={`${data.blockExplorerUrl}${data.transactionHash}`}
                    />

                    <CopyButton
                      value={`${data.blockExplorerUrl}${data.transactionHash}`}
                      timeout={2000}
                    >
                      {({ copied, copy }) => (
                        <Tooltip
                          label={copied ? "Copied" : "Copy"}
                          className={classesIcons.copy}
                          withArrow
                          position="right"
                        >
                          <ActionIcon
                            color={
                              copied
                                ? theme.customColors.actionIconFirst
                                : theme.customColors.actionIconSecond
                            }
                            variant="subtle"
                            onClick={copy}
                          >
                            {copied ? (
                              <IconCheck className={classesIcons.width} />
                            ) : (
                              <IconCopy className={classesIcons.width} />
                            )}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Text>
                </Flex>

                <Flex
                  gap={theme.flex.gap.xxs}
                  justify={theme.flex.justify.between}
                  className="item download-pdf-item"
                  align={theme.flex.align.end}
                >
                  {/* PDF display_timezone is now always UTC (formatted server-side). */}
                  <DownloadStampCertificate data={data} />
                </Flex>
              </Box>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      </Box>

      <Box className="file-found-preview-container">
        <Box className="file-found_bottom">
          <Text className="file-found_bottom-title">
            Alternative Authenticity Validation Methods
          </Text>
          <Text className="file-found_bottom-text">
            <Text component="b">Primary:</Text> Download content, then load info{" "}
            <CustomAnchor
              href="https://app.vbase.com/verify/"
              text="app.vbase.com/verify"
              componentClasses="link"
            />
          </Text>

          <Text className="file-found_bottom-text">
            <Text component="b">Secondary:</Text> Learn how to verify via{" "}
            <CustomAnchor
              href="https://docs.vbase.com/getting-started/verification-methods"
              target="_blank"
              text="third party tools"
              componentClasses="link"
            />
            ,{" "}
            <CustomAnchor
              href="https://docs.vbase.com/getting-started/verification-methods#api-methods"
              target="_blank"
              text="API"
              componentClasses="link"
            />{" "}
            and{" "}
            <CustomAnchor
              href="https://docs.vbase.com/getting-started/verification-methods#without-vbase-tools"
              target="_blank"
              text="other methods"
              componentClasses="link"
            />
          </Text>

          <Text className="file-found_bottom-text-last">
            Want Verifiable Authenticity Records for Your Content?
          </Text>
        </Box>
      </Box>
      <TriggerHelp
        helpIsActive={helpIsActive}
        isNearBottom={isNearBottom}
        isAuthenticated="true"
        isOpenNotification={false}
        handleHelpTriggerClick={handleHelpTriggerClick}
      />
    </Box>
  );
};

FileFound.propTypes = {
  data: PropTypes.shape({
    objectCid: PropTypes.string,
    time: PropTypes.string,
    user: PropTypes.string,
    user_id: PropTypes.string,
    authenticated: PropTypes.bool,
    collection_name: PropTypes.string,
    timedelta: PropTypes.string,
    collection_hash: PropTypes.string,
    blockExplorerUrl: PropTypes.string,
    transactionHash: PropTypes.string,
    timestamp: PropTypes.string,
    display_timezone: PropTypes.string,
    file_url: PropTypes.string,
    user_at_stamp_time: PropTypes.string,
    verified_at_stamp_time: PropTypes.bool,
    verified_since_at_stamp_time: PropTypes.string,
    user_changed_since_stamp: PropTypes.bool,
    identity_changed_since_stamp: PropTypes.bool,
  }).isRequired,
  staticBasePath: PropTypes.string.isRequired,
  showVerifyStamp: PropTypes.bool,
};
export default FileFound;
